/*
 * script-navigation.js — header / nav-bar coordinator.
 *
 * Owns cross-cutting state for the index header chrome: menu fade-in as the
 * user scrolls past the spacer, logo ↔ filter-bar swap, and modal-aware
 * restore from product-click navigation.
 *
 * Public API: window.HeaderState
 *
 *   state           — read-only snapshot (atTop, filtersShown, modalOpen,
 *                     cartOpen, menuLocked)
 *   subscribe(fn)   — fn(state, prevState) on every mutation; returns
 *                     unsubscribe function
 *   revealFilters({callback})       — logo → filter-bar transition
 *   setNameDisplay(label)           — pin the NAME pill to an arbitrary label
 *   enterModal({fromProductClick, callback}) — open modal (optionally with
 *                                              bar transition)
 *   exitModal()                     — restore bar from product-click modal
 *   openCart() / closeCart()        — cart drawer (delegated to window.Cart
 *                                     for now; phase 4 will fold the
 *                                     implementation in)
 *   revealMain()                    — reveal main content (used by logo click)
 *
 * Legacy internal-only globals still in use:
 *   window._modalOpenedFromProductClick  — set by script-index-product.js
 *                                          synthetic-restore path (608-609)
 *   window._preProductClickBarState      — paired with the flag above
 *   window._revealMain                   — set by script-index-content.js
 *   window._onFilterBarVisible /
 *   window._onScrolledToTop              — bridge between this file's two
 *                                          DOMContentLoaded blocks
 *
 * Load order: theme.liquid loads this in <head> before script-cart.js and
 * before sections/index-filters.liquid's script tag, so all DOMContentLoaded
 * handlers register in dependency order.
 */

(function () {
  var state = {
    atTop: true,         // window.scrollY === 0 (sentinel observer)
    filtersShown: true,  // filter buttons are always shown when the bar is visible
    modalOpen: false,    // #collection-modal open
    cartOpen: false,     // cart drawer open
    menuLocked: false,   // force-show menu regardless of scroll
  };

  var subscribers = [];

  function snapshot() {
    return {
      atTop: state.atTop,
      filtersShown: state.filtersShown,
      modalOpen: state.modalOpen,
      cartOpen: state.cartOpen,
      menuLocked: state.menuLocked,
    };
  }

  function mutate(updater) {
    var prev = snapshot();
    updater(state);
    var next = snapshot();
    subscribers.forEach(function (fn) {
      try { fn(next, prev); } catch (e) { /* subscriber threw — keep going */ }
    });
  }

  // No-op stubs for pre-DOMContentLoaded calls. Real implementations are
  // assigned inside the main DOMContentLoaded block where they have closure
  // access to local DOM references.
  function noop() {}
  function noopWithCallback(opts) {
    if (opts && typeof opts.callback === "function") opts.callback();
  }

  window.HeaderState = {
    get state() { return snapshot(); },

    subscribe: function (fn) {
      if (typeof fn !== "function") return noop;
      subscribers.push(fn);
      return function () {
        var i = subscribers.indexOf(fn);
        if (i !== -1) subscribers.splice(i, 1);
      };
    },

    // These get replaced inside DOMContentLoaded — until then they're safe
    // no-ops (the enterModal stub still fires the callback so callers that
    // expect doOpen() to run during pre-DOMContentLoaded paths don't strand).
    revealFilters: noopWithCallback,
    setNameDisplay: noop,
    enterModal: noopWithCallback,
    exitModal: noop,
    revealMain: noop,

    // Cart drawer — owned by the coordinator. script-cart.js binds click
    // handlers that call these methods; visual state is driven entirely
    // through the body.cart-open class (CSS handles the rest).
    openCart: function () {
      if (state.cartOpen) return;
      mutate(function (s) { s.cartOpen = true; });
      // Set the 0.54s transition inline so cart-open + cart-close fades stay
      // unified with the drawer's timing. Cleaned up in closeCart so
      // openProductModal's inline fade-in isn't fighting a stale value.
      ["index-logo", "cart-count"].forEach(function (id) {
        var el = document.getElementById(id);
        if (el) el.style.transition = "opacity 0.54s ease-in-out";
      });
      // The collection-modal title swaps INSTANTLY (no fade) when the cart
      // opens/closes — mirrors the modal's instant filter-bar → title swap, and
      // keeps it from lingering/fading over the cart logo.
      var titleSnap = document.getElementById("collection-modal-title");
      if (titleSnap) titleSnap.style.transition = "none";

      // Two-phase open so the opacity fade actually runs: cart-render first
      // (display:block + scroll-lock + JS placement at opacity 0), then cart-open
      // on a later frame flips opacity → the transition animates. Without the
      // gap the mobile layers go display:none→opacity:1 in one recalc and pop in.
      document.documentElement.classList.add("cart-render");
      document.body.classList.add("cart-render");
      if (window.HeaderState.positionCartLayer) window.HeaderState.positionCartLayer();
      var commit = function () {
        // Guard against a close that landed inside the two-frame arming window
        // (common on touch right after add-to-cart: a tap/synthetic-click closes
        // before the open commits). Without this, the stale commit re-adds
        // cart-open AFTER closeCart removed cart-render, leaving body.cart-open
        // present while state.cartOpen is false — the desync that makes the ×
        // stick and the next tap open-then-instantly-close.
        if (!state.cartOpen) return;
        document.documentElement.classList.add("cart-open");
        document.body.classList.add("cart-open");
        document.dispatchEvent(new CustomEvent("cart:opened"));
      };
      // Two rAFs guarantee the display/transform commit has painted before the
      // opacity flip; desktop has no display:none so it can flip same-tick.
      requestAnimationFrame(function () { requestAnimationFrame(commit); });
    },
    closeCart: function () {
      if (!state.cartOpen) return;
      mutate(function (s) { s.cartOpen = false; });
      // Drop cart-open → opacity fades out, but keep cart-render (display +
      // scroll-lock) until the fade finishes so it doesn't snap to display:none.
      document.documentElement.classList.remove("cart-open");
      document.body.classList.remove("cart-open");
      document.dispatchEvent(new CustomEvent("cart:closed"));
      setTimeout(function () {
        // Reopened during the fade-out window — keep it rendered so the new
        // openCart's cart-render isn't torn out from under it.
        if (state.cartOpen) return;
        document.documentElement.classList.remove("cart-render");
        document.body.classList.remove("cart-render");
        // Tear down spacers / clear transforms now that the layer is gone.
        if (window.HeaderState.positionCartLayer) window.HeaderState.positionCartLayer();
        // Clear the inline transition we set on open so other animations
        // (e.g., openProductModal's fade-in) aren't stuck with this value.
        ["index-logo", "cart-count", "collection-modal-title"].forEach(function (id) {
          var el = document.getElementById(id);
          if (el) el.style.transition = "";
        });
      }, 540);
    },

    // Internal — kept exposed so consumers can mirror their own state if
    // needed. Will be made private once the migration settles.
    _mutate: mutate,
  };
})();

/* ---------- Cart layer positioning (mobile only) ----------
 *
 * The nav chrome stays position:fixed (compositor-pinned → never jitters during
 * scroll). Only the MOBILE cart is position:absolute (the iOS fixed+blur fix),
 * so it must be placed at the current viewport offset via JS. The cart only ever
 * shows with body scroll LOCKED, so a single placement on open (plus resize) is
 * enough — there is NO scroll listener, so nothing can jitter.
 *
 * Layout: drawer + backdrop at translateY(scrollY) (the viewport); the two 100vh
 * spacers offset ±innerHeight so they mask overscroll above/below. Desktop keeps
 * the cart position:fixed, so transforms are cleared there.
 */
(function () {
  function isMobile() {
    return !!(window.matchMedia && window.matchMedia("(max-width: 573px)").matches);
  }

  // The two overscroll-mask spacers are NOT in the server-rendered DOM — they're
  // created here only while the mobile cart is open and torn down on close, so
  // they never linger in the body (and can't affect layout) when the cart is
  // shut. CSS (keyed by #cart-spacer-top / #cart-spacer-bottom) does the styling.
  function ensureSpacers(create) {
    ["cart-spacer-top", "cart-spacer-bottom"].forEach(function (id) {
      var el = document.getElementById(id);
      if (create) {
        if (!el) {
          el = document.createElement("div");
          el.id = id;
          // Bottom mask gets an inner blur div (the outer carries the main
          // cart-body blur/tint; the inner adds the checkout-style second blur
          // so the overscroll-reveal matches the doubled blur of the checkout
          // strip above it). Styled by .cart-spacer-blur in style-globals.
          if (id === "cart-spacer-bottom") {
            var inner = document.createElement("div");
            inner.className = "cart-spacer-blur";
            el.appendChild(inner);
          }
          document.body.appendChild(el);
        }
      } else if (el) {
        el.parentNode.removeChild(el);
      }
    });
  }

  function positionCartLayer() {
    // cart-render spans the whole open→fade→close-fade window (cart-open is just
    // the opacity phase), so place the layers whenever it's present.
    var open = document.body.classList.contains("cart-render");
    var y = window.scrollY || window.pageYOffset || 0;
    // The PDP modal pins <body> at position:fixed; top:-scrollY (iOS touch-scroll
    // fix), which makes window.scrollY read 0. If the cart is opened OVER the PDP,
    // recover the real viewport offset from the pin so the absolute cart layers
    // land at the viewport, not the document top. The body-pin shift (-Y) and this
    // translateY(+Y) cancel to viewport-0, exactly as the live-scrollY path does.
    if (document.body.style.position === "fixed" && document.body.style.top) {
      y = -parseInt(document.body.style.top, 10) || 0;
    }
    var h = window.innerHeight;
    function setT(id, val) {
      var el = document.getElementById(id);
      if (el) el.style.transform = val;
    }
    if (open && isMobile()) {
      ensureSpacers(true);
      var base = "translate3d(0," + y + "px,0)";
      setT("cart-backdrop", base);
      setT("cart-drawer", base);
      setT("cart-spacer-top", "translate3d(0," + (y - h) + "px,0)");
      setT("cart-spacer-bottom", "translate3d(0," + (y + h) + "px,0)");
    } else {
      // Closed, or desktop (where the cart is position:fixed): no transform.
      setT("cart-backdrop", "");
      setT("cart-drawer", "");
      ensureSpacers(false);
    }
  }

  window.HeaderState.positionCartLayer = positionCartLayer;
  // Place synchronously on open (scroll is locked, so the transform doesn't
  // animate — the CSS transition is opacity-only) and clear on close.
  document.addEventListener("cart:opened", positionCartLayer);
  document.addEventListener("cart:closed", positionCartLayer);
  window.addEventListener("resize", function () {
    if (document.body.classList.contains("cart-open")) positionCartLayer();
  });
})();

/* ---------- Block 1: scroll-position sentinel ----------
 *
 * Watches a 1px sentinel placed below the spacer threshold. When the user
 * scrolls past it, mirror state.atTop = false and fade the menu in. When
 * back at the top, the inverse. Notifies block 2 via the existing
 * _onFilterBarVisible / _onScrolledToTop bridge.
 */
document.addEventListener('DOMContentLoaded', function () {
  var menu = document.getElementById('index-menu');
  if (!menu) return;

  menu.style.opacity = '0';
  menu.style.pointerEvents = 'none';

  var sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:35px;left:0;width:1px;height:1px;pointer-events:none;';
  document.body.appendChild(sentinel);

  var triggered = false;
  var observer = new IntersectionObserver(function (entries) {
    var modalEl = document.getElementById('collection-modal');
    if (modalEl && modalEl.style.display === 'block') return;
    entries.forEach(function (entry) {
      var locked = window.HeaderState.state.menuLocked;
      if (!entry.isIntersecting) {
        // Scrolled away from top.
        window.HeaderState._mutate(function (s) { s.atTop = false; });
        // Re-measure filter-group widths now the bar is about to show: if the
        // initial measure ran while the bar was display:none (cart/PDP open at
        // load), the groups carry 0 widths and would stack. No-op when widths
        // are already correct.
        if (window.__measureFilterWidths) window.__measureFilterWidths();
        if (!locked) {
          // Only animate the bar in if it isn't already showing. Returning from a
          // filter/VIEW-ALL modal that was opened while scrolled, the bar never
          // left (opacity stays 1), but the modal-open guard above desyncs this
          // observer: buildModalGrid's scrollTo(0,0) re-enters the sentinel while
          // the modal is up (guarded → skipped), then closeModal's scroll-restore
          // re-crosses it with the modal hidden, firing this branch. Re-running the
          // 0→1 fade on an already-visible bar is the flash. Guard on opacity.
          if (menu.style.opacity !== '1') {
            menu.style.opacity = '0';
            requestAnimationFrame(function () {
              menu.style.transition = 'opacity 0.3s ease';
              menu.style.opacity = '1';
              menu.style.pointerEvents = 'auto';
              setTimeout(function () { menu.style.transition = ''; }, 300);
            });
          } else {
            menu.style.pointerEvents = 'auto';
          }
        }
        if (!triggered) {
          triggered = true;
          if (window._onFilterBarVisible) window._onFilterBarVisible();
        }
      } else {
        // Scrolled back to top.
        window.HeaderState._mutate(function (s) { s.atTop = true; });
        // During a back/reload restore the page sits at scroll 0 for a moment
        // before the browser applies the saved (scrolled) position. That transient
        // fires this branch and fades the bar out — then restoration scrolls down
        // and the other branch fades it back in: the visible→hidden→visible bar
        // flicker. While reconcileOnLoad is still settling, don't react to it; the
        // load reconcile owns the chrome until the real scroll position lands.
        if (window.__navSettling) return;
        if (!locked) {
          menu.style.transition = 'opacity 0.3s ease';
          menu.style.opacity = '0';
          menu.style.pointerEvents = 'none';
          setTimeout(function () { menu.style.transition = ''; }, 300);
        }
        if (triggered) {
          triggered = false;
          if (window._onScrolledToTop) window._onScrolledToTop();
        }
      }
    });
  }, { threshold: 0 });

  observer.observe(sentinel);
});

/* ---------- Block 2: main coordinator ----------
 *
 * Owns the logo ↔ filter-bar swap on scroll and the modal-aware restore
 * from product-click navigation. Wires HeaderState's transition methods to
 * closures that have access to all relevant DOM references.
 */
document.addEventListener('DOMContentLoaded', function () {
  var menu = document.getElementById('index-menu');
  if (!menu) return;
  var filterBar = document.getElementById('filter-bar');
  var indexLogo = document.getElementById('index-logo');

  var hideLogoTimer = null;

  // ---------- State-mirror helpers ----------
  // Centralize the HeaderState updates so every mutation site stays in sync.
  // Subscribers see menuLocked / modalOpen transitions without needing to
  // grep for raw assignments.
  function setMenuLocked(v) {
    var locked = !!v;
    window.HeaderState._mutate(function (s) { s.menuLocked = locked; });
  }
  function setModalOpen(v) {
    var open = !!v;
    window.HeaderState._mutate(function (s) { s.modalOpen = open; });
  }

  // Persist the logo-hidden state in TWO places so a back/reload never flashes:
  //  • sessionStorage flag → read by theme.liquid's pre-paint script on a real
  //    reload (DOM is re-rendered fresh, no class yet).
  //  • the .nav-hidden class on <html>/<body> → bfcache restores DOM classes, so
  //    on a back-restore the CSS (pre-load.liquid) hides the logo on the very
  //    first frame, before any JS runs. The class uses visibility:hidden (not
  //    opacity) so the restore can't show a fade — and it's only added once the
  //    logo is already settled-hidden (end of fade / instant hides) and removed
  //    before any fade-in, so it never fights the in-session opacity animations.
  function navFlag(hidden) {
    try { sessionStorage.setItem('cl-nav-hidden', hidden ? '1' : '0'); } catch (e) {}
  }
  function setNavHidden(hidden) {
    navFlag(hidden);
    document.documentElement.classList.toggle('nav-hidden', hidden);
    if (document.body) document.body.classList.toggle('nav-hidden', hidden);
    if (!hidden) {
      var pre = document.getElementById('cl-nav-hide'); // legacy style, if any
      if (pre) pre.remove();
    }
  }

  // ---------- Internal transitions (DOM only — no state mirror here) ----------
  function hideLogo() {
    if (!indexLogo) return;
    // Persist the intent now, but add the .nav-hidden class only AFTER the fade
    // (in the timer) — adding it up front would apply CSS opacity:0 before the
    // inline transition starts and snap the logo out instead of fading it.
    navFlag(true);
    if (hideLogoTimer) { clearTimeout(hideLogoTimer); hideLogoTimer = null; }
    indexLogo.style.pointerEvents = 'none';
    indexLogo.style.transition = 'opacity 0.3s ease';
    indexLogo.style.opacity = '0';
    hideLogoTimer = setTimeout(function () {
      hideLogoTimer = null;
      indexLogo.style.visibility = 'hidden';
      indexLogo.style.opacity = '';
      indexLogo.style.transition = '';
      setNavHidden(true); // resting hidden → add the restorable class
    }, 300);
  }

  function showLogo() {
    if (!indexLogo) return;
    // Suppressed while a timed reveal (footer-logo → home-top fade) owns the
    // logo: the scroll-to-top there fires the sentinel, which would otherwise
    // call this 0.3s fade and stomp the 0.9s grid-synced fade.
    if (window.__logoTimedRevealActive) return;
    // Only reveal if we're ACTUALLY at the top now. _onScrolledToTop schedules
    // this on a 300ms delay; on a back-restore the page sits at scroll 0 for a
    // moment (sentinel intersects → _onScrolledToTop fires), then restoration
    // scrolls down — without this guard the stale timer fades the logo in over
    // the filter buttons even though we landed scrolled. (Sentinel is at 35px.)
    if ((window.scrollY || window.pageYOffset || 0) > 35) return;
    setNavHidden(false); // remove class first so the inline fade-in is visible
    if (hideLogoTimer) { clearTimeout(hideLogoTimer); hideLogoTimer = null; }
    indexLogo.style.visibility = '';
    indexLogo.style.pointerEvents = '';
    indexLogo.style.opacity = '0';
    requestAnimationFrame(function () {
      indexLogo.style.transition = 'opacity 0.3s ease';
      indexLogo.style.opacity = '1';
      setTimeout(function () { indexLogo.style.transition = ''; }, 300);
    });
  }

  // Reveal the logo with a caller-chosen duration (used by the footer-logo
  // home-top fade so the logo eases in on the SAME 0.9s curve as the grid,
  // instead of the default 0.3s). Sets a guard so the scroll sentinel's showLogo
  // can't override mid-fade.
  var logoTimedRevealTimer = null;
  window.__revealLogoTimed = function (durationMs) {
    if (!indexLogo) return;
    window.__logoTimedRevealActive = true;
    if (hideLogoTimer) { clearTimeout(hideLogoTimer); hideLogoTimer = null; }
    if (logoTimedRevealTimer) { clearTimeout(logoTimedRevealTimer); logoTimedRevealTimer = null; }
    setNavHidden(false); // remove .nav-hidden so the fade is visible
    indexLogo.style.transition = 'none';
    indexLogo.style.visibility = '';
    indexLogo.style.pointerEvents = '';
    indexLogo.style.opacity = '0';
    requestAnimationFrame(function () {
      indexLogo.style.transition = 'opacity ' + (durationMs / 1000) + 's ease-in-out';
      indexLogo.style.opacity = '1';
      logoTimedRevealTimer = setTimeout(function () {
        indexLogo.style.transition = '';
        window.__logoTimedRevealActive = false;
        logoTimedRevealTimer = null;
      }, durationMs + 50);
    });
  };

  // ---------- Block-1 → block-2 bridge (still via window for now) ----------
  window._onFilterBarVisible = function () { hideLogo(); };
  window._onScrolledToTop = function () {
    setMenuLocked(false);
    menu.style.transition = 'opacity 0.3s ease';
    menu.style.opacity = '0';
    menu.style.pointerEvents = 'none';
    setTimeout(function () { menu.style.transition = ''; }, 300);
    setTimeout(showLogo, 300);
  };

  // ---------- Click handlers ----------
  if (indexLogo) {
    indexLogo.style.cursor = 'pointer';
    indexLogo.addEventListener('click', function () {
      window.HeaderState.revealMain();
      hideLogo();
      setMenuLocked(true);
      // Bar is about to swap in — make sure group widths are valid (they may be
      // 0 if the only prior measure ran while the bar was display:none).
      if (window.__measureFilterWidths) window.__measureFilterWidths();
      setTimeout(function () {
        menu.style.transition = 'opacity 0.4s ease';
        menu.style.opacity = '1';
        menu.style.pointerEvents = 'auto';
        setTimeout(function () { menu.style.transition = ''; }, 400);
      }, 300);
    });
  }

  // ---------- HeaderState method implementations ----------
  // Real impls (replacing the no-op stubs from the top-of-file IIFE).

  window.HeaderState.revealFilters = function (opts) {
    opts = opts || {};
    // Snapshot pre-transition bar state so we can restore it on modal close.
    // Only `atTop` matters now: the bar always shows its filter buttons when
    // visible, so the close path just decides whether to fall back to the
    // logo (was at top) or leave the bar up (was scrolled).
    window._preProductClickBarState = {
      atTop: window.scrollY === 0,
    };
    if (indexLogo) {
      indexLogo.style.transition = 'none';
      indexLogo.style.opacity = '0';
      indexLogo.style.visibility = 'hidden';
    }
    // The logo is now hidden because a modal/filter bar took over. Persist that
    // so a reload or bfcache-back into this collection/VIEW ALL view re-hides the
    // logo pre-paint instead of flashing it (revealFilters is the modal path that
    // hides the logo WITHOUT going through hideLogo, which is what set the flag).
    setNavHidden(true);
    setMenuLocked(true);
    var filterSection = document.getElementById('shopify-section-index-filters');
    if (filterSection) filterSection.style.visibility = '';
    menu.style.transition = 'none';
    menu.style.visibility = 'visible';
    menu.style.opacity = '1';
    menu.style.pointerEvents = 'auto';
    // Now the bar is visible, (re)measure group widths so they don't stack if
    // the initial measure ran while it was hidden (deep-link/PDP load).
    if (window.__measureFilterWidths) window.__measureFilterWidths();
    if (typeof opts.callback === 'function') opts.callback();
  };

  window.HeaderState.enterModal = function (opts) {
    opts = opts || {};
    setModalOpen(true);
    if (opts.fromProductClick) {
      window._modalOpenedFromProductClick = true;
      window.HeaderState.revealFilters({ callback: opts.callback });
      return;
    }
    if (typeof opts.callback === 'function') opts.callback();
  };

  window.HeaderState.exitModal = function () {
    setModalOpen(false);
    if (!window._modalOpenedFromProductClick) return;
    window._modalOpenedFromProductClick = false;
    var snap = window._preProductClickBarState;
    window._preProductClickBarState = null;
    if (!snap) return;
    if (!snap.atTop) return; // bar was already visible from scrolling — leave it

    // User opened the modal from the top (logo state): hide the bar and snap
    // the logo back instantly (no fade, to avoid a flash).
    setMenuLocked(false);
    // Back at the bare home/top → logo is shown again. Clear the persisted hide
    // (revealFilters set it on open) so a later reload doesn't pre-paint-hide the
    // logo and then flash it back in.
    setNavHidden(false);
    menu.style.transition = 'none';
    menu.style.opacity = '0';
    menu.style.pointerEvents = 'none';
    requestAnimationFrame(function () { menu.style.transition = ''; });
    if (indexLogo) {
      if (hideLogoTimer) { clearTimeout(hideLogoTimer); hideLogoTimer = null; }
      indexLogo.style.transition = 'none';
      indexLogo.style.visibility = '';
      indexLogo.style.opacity = '1';
      indexLogo.style.pointerEvents = '';
      requestAnimationFrame(function () { indexLogo.style.transition = ''; });
    }
    // Scroll restoration is owned by the index scripts' closeModal paths
    // (they restore window.__indexHomeScrollY). HeaderState no longer touches
    // document scroll — the collection modal is now an in-flow body swap, so
    // hardcoding scrollTo(0,0) here would clobber the saved home position.
  };

  // Pin the NAME pill to an arbitrary label — used for collections not in
  // the filter menu. Moved here from script-index-filters.js so all
  // nav-chrome mutations live in one place.
  window.HeaderState.setNameDisplay = function (labelText) {
    var nameDropdown = document.querySelector("[data-filter='name']");
    if (!nameDropdown || !labelText) return;
    var container = nameDropdown.closest('.dropdown-button-container');
    var filterGroup = container ? container.closest('.filter-group') : null;
    var displaySpan = container ? container.querySelector('.selected-filter') : null;
    var placeholder = container ? container.querySelector('.filter-placeholder') : null;
    if (!container || !displaySpan) return;

    displaySpan.textContent = labelText;
    if (placeholder) placeholder.style.display = 'none';

    var textWidth = displaySpan.offsetWidth;
    container.style.width = textWidth + 'px';
    nameDropdown.style.width = textWidth + 'px';
    if (filterGroup) filterGroup.style.width = textWidth + 'px';

    filterBar.classList.add('name-active');
    filterBar.classList.remove('category-finish-active');
  };

  window.HeaderState.revealMain = function () {
    if (typeof window._revealMain === 'function') window._revealMain();
  };

  // ---------- back-nav chrome re-sync ----------
  // On mobile, returning to the index from a hard-linked page (information,
  // factory shop, etc.) leaves the logo ↔ filter-bar state stale: scrolled
  // down, you end up with the logo overlapping the filter buttons. Two distinct
  // restore paths need correcting (handled below): a bfcache HIT (DOM restored,
  // observer never re-fires) and a bfcache MISS (real reload whose observer can
  // fire at the top before scroll restoration lands). syncChromeToScroll()
  // recomputes the chrome instantly against the actual scroll position.

  // hideOnly=true (used on every back/reload restore): only ever HIDE the logo
  // when scrolled — never force-show it. On a restore the persisted .nav-hidden
  // class/flag is already correct, so the only safe correction is to hide if we
  // turn out to be scrolled. Force-showing here is what caused the home-grid
  // flash: a pre-restoration scrollY read of 0 took the "at top" branch and
  // removed the class, then the real scroll landed and re-hid it. (Collection /
  // VIEW ALL never flashed only because menuLocked made this bail entirely.)
  function syncChromeToScroll(hideOnly) {
    if (window.HeaderState.state.modalOpen) return;
    if (window.HeaderState.state.menuLocked) return;
    // Sentinel sits at top:35px; past it means we're scrolled into the grid.
    var scrolled = (window.scrollY || window.pageYOffset || 0) > 35;
    if (!scrolled && hideOnly) return; // restore already shows the logo — leave it
    if (scrolled) {
      // Hide logo, show filter bar — no fade, the restore is already visible.
      setNavHidden(true);
      if (hideLogoTimer) { clearTimeout(hideLogoTimer); hideLogoTimer = null; }
      if (indexLogo) {
        indexLogo.style.transition = 'none';
        indexLogo.style.opacity = '0';
        indexLogo.style.visibility = 'hidden';
        indexLogo.style.pointerEvents = 'none';
        requestAnimationFrame(function () { indexLogo.style.transition = ''; });
      }
      menu.style.transition = 'none';
      menu.style.visibility = 'visible';
      menu.style.opacity = '1';
      menu.style.pointerEvents = 'auto';
      requestAnimationFrame(function () { menu.style.transition = ''; });
      window.HeaderState._mutate(function (s) { s.atTop = false; });
    } else {
      // At top — show logo, hide bar. setNavHidden(false) ALSO removes the
      // .nav-hidden class; without that its CSS opacity:0 would keep the logo
      // hidden (the stale-flag-at-top case on a fresh navigation / back-restore).
      setNavHidden(false);
      if (hideLogoTimer) { clearTimeout(hideLogoTimer); hideLogoTimer = null; }
      if (indexLogo) {
        indexLogo.style.transition = 'none';
        indexLogo.style.visibility = '';
        indexLogo.style.opacity = '1';
        indexLogo.style.pointerEvents = '';
        requestAnimationFrame(function () { indexLogo.style.transition = ''; });
      }
      menu.style.transition = 'none';
      menu.style.opacity = '0';
      menu.style.pointerEvents = 'none';
      requestAnimationFrame(function () { menu.style.transition = ''; });
      window.HeaderState._mutate(function (s) { s.atTop = true; });
    }
  }

  // Reconcile the chrome to the SETTLED scroll position once per load.
  //
  // The trap that caused the persistent home-grid flicker: scroll restoration is
  // async (fires AFTER load/pageshow) and iOS Safari frequently MISREPORTS a back
  // navigation's type as 'navigate'. So neither an early scrollY read nor
  // performance navType can tell us "are we at the top?" — an early read returns
  // 0, we'd show the logo, then the restore scrolls and re-hides it (the flicker).
  //
  // Decide from BEHAVIOUR instead: if a scroll fires shortly after load, the page
  // was restored scrolled → only ever HIDE (hideOnly, keeps the logo hidden, no
  // flash). If NO scroll fires, we're genuinely at the top → fully reconcile,
  // which may show the logo / clear a stale pre-paint hide. The scroll listener
  // This is a ONE-SHOT settle, not a permanent scroll handler — the
  // IntersectionObserver (Block 1) owns ongoing scroll show/hide. The listener
  // MUST be removed at the end of the window: on a bfcache restore the scroll is
  // re-applied silently (no scroll event), so onScroll never fires and, if left
  // armed, lingers until the NEXT scroll — which turned out to be the one during
  // a later product-modal open, where it mutated the chrome mid-animation (the
  // "back from /page/, then click a product → grid shifts / logo flashes" jank).
  function reconcileOnLoad() {
    window.__navSettling = true; // suppress the observer's transient reactions
    var settled = false;
    function onScroll() {
      settled = true;
      window.removeEventListener('scroll', onScroll);
      syncChromeToScroll(true); // restoration moved us → hide-only
      window.__navSettling = false;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(function () {
      window.removeEventListener('scroll', onScroll); // never let it linger
      if (!settled) syncChromeToScroll(false); // no scroll fired → at top; may show
      window.__navSettling = false;
    }, 300);
  }

  // Synchronously force the logo to the persisted hidden/shown state. The inline
  // pre-paint script in theme.liquid only runs on a real load, NOT on a bfcache
  // restore — so on bfcache the logo could show for a frame before reconcileOnLoad
  // settles. pageshow fires BEFORE the restored frame paints, so applying the
  // flag here kills that flash.
  function applyNavHiddenInstant() {
    if (!indexLogo) return;
    // NOTE: do NOT bail on modalOpen/menuLocked. A collection / VIEW ALL modal
    // restore is exactly the case that flashes, and in that state the logo SHOULD
    // be hidden too — this function only ever HIDES (when the flag says so), so
    // applying it is always safe; reconcileOnLoad (which respects those flags)
    // does the directional reconcile afterward.
    var hidden = false;
    try { hidden = sessionStorage.getItem('cl-nav-hidden') === '1'; } catch (e) {}
    if (!hidden) return; // shown state is the restored default; leave it alone
    if (hideLogoTimer) { clearTimeout(hideLogoTimer); hideLogoTimer = null; }
    indexLogo.style.transition = 'none';
    indexLogo.style.opacity = '0';
    indexLogo.style.visibility = 'hidden';
    indexLogo.style.pointerEvents = 'none';
    menu.style.transition = 'none';
    menu.style.visibility = 'visible';
    menu.style.opacity = '1';
    menu.style.pointerEvents = 'auto';
  }

  // One handler for BOTH initial load and bfcache restore (pageshow always fires,
  // after 'load'). On bfcache, hide synchronously first per the persisted flag so
  // the restored frame can't show the logo, then reconcile once scroll settles.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) applyNavHiddenInstant();
    reconcileOnLoad();
  });
});
